import axios from 'axios';

// Use local backend API for employee management
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// External Lunipin API for other features (fallback) - use environment variable
const LUNIPIN_API = process.env.REACT_APP_LUNIPIN_API_URL || `${BACKEND_URL}/api`;

// Local API for employees and auth
const localApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// External API for Lunipin app data
const externalApi = axios.create({
  baseURL: LUNIPIN_API,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
const addAuthToken = (config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

localApi.interceptors.request.use(addAuthToken);
externalApi.interceptors.request.use(addAuthToken);

// Handle auth errors - but not for admin/demo tokens
const handleAuthError = (error) => {
  const token = localStorage.getItem('admin_token');
  const isProtectedToken = token && (token.startsWith('demo-') || token.startsWith('admin-'));
  
  if (error.response?.status === 401 && !isProtectedToken) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

localApi.interceptors.response.use((r) => r, handleAuthError);
externalApi.interceptors.response.use((r) => r, handleAuthError);

// ==================== AUTH ====================
export const login = (email, password) => localApi.post('/admin/login', { email, password });
export const logout = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
};

// ==================== EMPLOYEES ====================
export const getEmployees = (params) => localApi.get('/employees', { params });
export const getEmployee = (id) => localApi.get(`/employees/${id}`);
export const createEmployee = (data) => localApi.post('/employees', data);
export const updateEmployee = (id, data) => localApi.put(`/employees/${id}`, data);
export const deleteEmployee = (id) => localApi.delete(`/employees/${id}`);
export const getDepartments = () => localApi.get('/departments');
export const getManagers = () => localApi.get('/managers');
export const getEmployeeStats = () => localApi.get('/dashboard/employee-stats');

// ==================== LUNIPIN DATA (External API with fallbacks) ====================

// Dashboard
export const getDashboardStats = () => externalApi.get('/dashboard');

// Users
export const getUsers = (params) => externalApi.get('/users', { params });
export const getUser = (id) => externalApi.get(`/users/${id}`);
export const updateUser = (id, data) => externalApi.put(`/users/${id}`, data);
export const deleteUser = (id) => externalApi.delete(`/users/${id}`);
export const banUser = (id) => externalApi.post(`/users/${id}/ban`);
export const unbanUser = (id) => externalApi.post(`/users/${id}/unban`);

// Teams
export const getTeams = (params) => externalApi.get('/teams', { params });
export const getTeam = (id) => externalApi.get(`/teams/${id}`);
export const deleteTeam = (id) => externalApi.delete(`/teams/${id}`);

// Events
export const getEvents = (params) => externalApi.get('/events', { params });
export const getEvent = (id) => externalApi.get(`/events/${id}`);
export const createEvent = (data) => externalApi.post('/events', data);
export const updateEvent = (id, data) => externalApi.put(`/events/${id}`, data);
export const deleteEvent = (id) => externalApi.delete(`/events/${id}`);

// Matches
export const getMatches = (params) => externalApi.get('/matches', { params });
export const getMatch = (id) => externalApi.get(`/matches/${id}`);

// Payments
export const getPayments = (params) => externalApi.get('/payments', { params });
export const getPayment = (id) => externalApi.get(`/payments/${id}`);

// Invoices
export const getInvoices = (params) => externalApi.get('/invoices', { params });
export const getInvoice = (id) => externalApi.get(`/invoices/${id}`);
export const generateInvoice = (paymentId) => externalApi.post('/invoices/generate', { payment_id: paymentId });

// Support Tickets
export const getTickets = (params) => externalApi.get('/tickets', { params });
export const getTicket = (id) => externalApi.get(`/tickets/${id}`);
export const updateTicket = (id, data) => externalApi.put(`/tickets/${id}`, data);
export const replyToTicket = (id, message) => externalApi.post(`/tickets/${id}/reply`, { message });

// Admins
export const getAdmins = (params) => externalApi.get('/admins', { params });
export const getAdmin = (id) => externalApi.get(`/admins/${id}`);
export const createAdmin = (data) => externalApi.post('/admins', data);
export const updateAdmin = (id, data) => externalApi.put(`/admins/${id}`, data);
export const deleteAdmin = (id) => externalApi.delete(`/admins/${id}`);

export default localApi;
